import { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import { formatSeasonName } from '@/lib/season/format';
import { pickVisibleSeasons } from '@/lib/season/visibility';
import {
  SEASON_STATUS_BADGE,
  SEASON_STATUS_TITLES,
  computeSeasonStatus,
} from '@/lib/season/computedStatus';
import { cn } from '@/lib/utils';
import {
  setScienceSeasonLens,
  useScienceSeasonLens,
} from '@/lib/science/seasonLens';

/**
 * Dropdown выбора активного сезона в шапке.
 * UX-аналог frontend/SeasonPicker.tsx (там был AntD Select с showSearch).
 * Здесь — DropdownMenu со списком.
 *
 * Два UAT-исправления 05.08 (AK-790 re-land #673, баги 1 и 2):
 *   · подпись — «Сезон 2026», а не голое «2026»: как сезон зовётся во всех
 *     остальных местах (formatSeasonName, единый DS-паттерн);
 *   · список ограничен окном visible_past/future_years из настройки компании,
 *     а не «все 51 из сида» — окно едет из SeasonProvider, один источник
 *     правды. Пока окно не загрузилось (`visibility === null`), показываем
 *     все сезоны, чтобы не оставить человека без переключателя на первом
 *     маунте.
 *
 * Статус сезона в строке — ВЫЧИСЛЯЕМЫЙ (канон терминологии v3 §5): «Идёт» у
 * текущего, «Завершён» у прошедших по датам рамки. Раньше здесь стоял бейдж
 * «активный» только у текущего, а прошедшие годы выглядели ровно как будущие —
 * агроном не видел, где заканчивается история и начинается план.
 *
 * Про утверждённость бюджета список сезонов (`GET /session/seasons`) ничего не
 * знает, поэтому у будущих сезонов бейджа нет: «Запланирован / Не запланирован»
 * живут там, где эти данные есть, — в разделе планирования.
 */
export function SeasonPicker() {
  const { season, seasons, visibility, loading, setSeasonByCode } =
    useActiveSeason();
  // AK-938: в «Науке» есть пункт «Все сезоны» (фильтр списка опытов, сезон
  // сессии не меняет). В остальных разделах линза не активна — всё как было.
  const science = useScienceSeasonLens();
  const allSeasons = science.active && science.lens === 'all';

  const sorted = useMemo(
    () =>
      pickVisibleSeasons(seasons, visibility, new Date().getFullYear(), season?.code),
    [seasons, visibility, season?.code],
  );

  const label = allSeasons
    ? 'Все сезоны'
    : season
    ? formatSeasonName(season.code, season.year)
    : loading
      ? 'Сезон…'
      : 'Сезон';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="min-w-[100px] justify-between"
        >
          <span>{label}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[320px] overflow-y-auto">
        {science.active && (
          <DropdownMenuItem
            onSelect={() => setScienceSeasonLens('all')}
            className={cn(allSeasons && 'bg-accent')}
          >
            Все сезоны
          </DropdownMenuItem>
        )}
        {sorted.map((s) => {
          const status = computeSeasonStatus({
            startsOn: s.startsOn,
            endsOn: s.endsOn,
            year: s.year,
            isCurrent: s.isCurrent,
          });
          return (
          <DropdownMenuItem
            key={s.code}
            onSelect={async () => {
              if (science.active) setScienceSeasonLens('session');
              try {
                await setSeasonByCode(s.code);
              } catch {
                /* tolerate — api.ts обрабатывает 401/423 */
              }
            }}
            className={cn(
              'flex items-center justify-between gap-3',
              !allSeasons && season?.code === s.code && 'bg-accent',
            )}
          >
            <span>{formatSeasonName(s.code, s.year)}</span>
            {status && (
              <Badge
                variant={SEASON_STATUS_BADGE[status]}
                className="text-[10px] px-1.5 py-0"
              >
                {SEASON_STATUS_TITLES[status]}
              </Badge>
            )}
          </DropdownMenuItem>
          );
        })}
        {sorted.length === 0 && !loading && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Нет сезонов
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default SeasonPicker;
